import { Link } from "react-router";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  ClipboardList,
  Handshake,
  TrendingUp,
} from "lucide-react";
import { buildMergeFieldContext } from "@colab/shared";
import { useSession } from "@/lib/auth-context";
import { cn, formatCurrencyEUR } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { MetricCard } from "@/components/shared/metric-card";
import { AnnouncementStrip } from "./_components/announcement-strip";
import { WelcomeBanner } from "./_components/welcome-banner";
import { FirstRunChecklist } from "./_components/first-run-checklist";
import { GoalsWidget } from "./_components/goals-widget";
import { LevelUpWidget } from "./_components/level-up-widget";
import { RewardsWidget } from "./_components/rewards-widget";
import { FaqAccordion } from "./_components/faq-accordion";
import { useCurrentPartner } from "./use-current-partner";
import { usePartnerDealSummary } from "./use-deal-summary";
import { usePortalContent } from "./use-portal-content";
import { usePortalFaq } from "./use-portal-faq";
import { usePortalGoals } from "./use-portal-goals";
import { useTierProgram } from "./use-tier-program";

// Port of /app/portal/dashboard/page.tsx. Seven parallel queries land via
// independent TanStack Query hooks; each card shows its own skeleton while
// loading so the page never falls back to a full-screen spinner. Merge-field
// context is built client-side from the partner row + session full name —
// the server returns the raw resolved content and the page renders the
// substitution at view time.
export function PortalDashboardPage() {
  const session = useSession();

  const partnerQuery = useCurrentPartner();
  const dealsQuery = usePartnerDealSummary();
  const contentQuery = usePortalContent();
  const faqQuery = usePortalFaq(5);
  const goalsQuery = usePortalGoals();
  const tierQuery = useTierProgram();

  const partner = partnerQuery.data ?? null;
  const deals = dealsQuery.data ?? null;
  const content = contentQuery.data ?? null;
  const faqItems = faqQuery.data ?? [];
  const goals = goalsQuery.data ?? [];
  const tierData = tierQuery.data ?? null;

  const mergeContext = buildMergeFieldContext({
    partner: {
      name: partner?.company_name ?? null,
      tier: partner?.tier ?? null,
      partner_type: partner?.partner_type ?? null,
    },
    partnerUserFullName: session?.fullName ?? null,
  });

  const showLevelUp = tierData?.program && tierData.tiers.length > 0;
  const showRow = goals.length > 0 || showLevelUp;

  return (
    <div>
      <AnnouncementStrip />

      {contentQuery.isLoading || partnerQuery.isLoading ? (
        <Skeleton className="mb-7 h-44 w-full rounded-2xl" />
      ) : content ? (
        <WelcomeBanner
          resolution={content.welcome}
          mergeContext={mergeContext}
          fallbackName={mergeContext.partner.first_name ?? partner?.company_name ?? null}
        />
      ) : null}

      {partner && deals ? (
        <FirstRunChecklist
          partnerId={partner.id}
          profileComplete={Boolean(session?.fullName?.trim())}
          integrationsSet={(partner.integrates_with ?? []).length > 0}
          firstDealSubmitted={deals.myDeals > 0}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {dealsQuery.isLoading || !deals ? (
          <>
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
          </>
        ) : (
          <>
            <MetricCard
              label="My deals"
              value={String(deals.myDeals)}
              icon={Handshake}
              hint="Deals you referred to Bookboost"
            />
            <MetricCard
              label="Active deals"
              value={String(deals.activeDeals)}
              icon={TrendingUp}
              hint="In pipeline now"
            />
            <MetricCard
              label="Wins"
              value={String(deals.wonDeals)}
              icon={ArrowRight}
              hint={deals.wonValueEur > 0 ? formatCurrencyEUR(deals.wonValueEur) : "Closed-won deals"}
            />
          </>
        )}
      </div>

      {deals && deals.introCount > 0 && (
        <div className="mt-3 rounded-lg border border-dashed border-border/70 bg-muted/20 px-4 py-2.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Bookboost referred to you:</span>{" "}
          <span className="tabular-nums">{deals.introCount}</span>{" "}
          {deals.introCount === 1 ? "lead" : "leads"}.{" "}
          <Link to="/portal/deals" className="font-medium text-accent hover:underline">
            See details
          </Link>
        </div>
      )}

      {partner ? (
        <div className="mt-8">
          <RewardsWidget
            pendingEur={partner.rewards_pending_eur}
            paidEur={partner.rewards_paid_eur}
          />
        </div>
      ) : null}

      {goalsQuery.isLoading || tierQuery.isLoading ? (
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      ) : showRow && content ? (
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <GoalsWidget goals={goals} />
          <LevelUpWidget
            program={tierData?.program ?? null}
            tiers={tierData?.tiers ?? []}
            rows={tierData?.rows ?? []}
            cells={tierData?.cells ?? []}
            currentTierId={tierData?.currentTierId ?? null}
            resolution={content.level_up}
          />
        </div>
      ) : null}

      <section className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <QuickLink
          to="/portal/analytics"
          icon={BarChart3}
          title="Analytics"
          body="Pipeline, content engagement, and activity over time."
          cta="See details"
          accent
        />
        <QuickLink
          to="/portal/deals"
          icon={Handshake}
          title="My deals"
          body="Deals Bookboost has attributed to your team in HubSpot."
          cta="View all"
          accent
        />
        <QuickLink
          to="/portal/resources"
          icon={BookOpen}
          title="Resources"
          body="Decks, battlecards, case studies, and playbooks."
          cta="Browse library"
        />
        <QuickLink
          to="/portal/journeys"
          icon={ClipboardList}
          title="Action plan"
          body="Your assigned partnership playbook and tasks."
          cta="Open plan"
        />
      </section>

      <div className="mt-8">
        {faqQuery.isLoading ? (
          <Skeleton className="h-56 w-full rounded-xl" />
        ) : (
          <FaqAccordion items={faqItems} showViewAll heading="Common questions" />
        )}
      </div>
    </div>
  );
}

type QuickLinkProps = {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  cta: string;
  /** Use the accent (teal) treatment for the icon + CTA. Default off. */
  accent?: boolean;
};

function QuickLink({ to, icon: Icon, title, body, cta, accent = false }: QuickLinkProps) {
  return (
    <Link
      to={to}
      className={cn(
        "group rounded-xl border bg-card p-5 transition",
        accent ? "hover:border-accent" : "hover:border-foreground/20",
      )}
    >
      <Icon className={cn("mb-3 h-5 w-5", accent ? "text-accent" : "text-muted-foreground")} />
      <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
      <span
        className={cn(
          "mt-3 inline-flex items-center gap-1 text-xs font-medium",
          accent ? "text-accent" : "text-foreground",
        )}
      >
        {cta}
        <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
